import { Typography } from '@euler/app/components/typography';
import { QuotationCatalogSubjectCell } from '@euler/app/flows/task/pages/quotation/components/QuotationCatalogSubjectCell';
import { useQuotationContext } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { useQuotationSelectionContext } from '@euler/app/flows/task/pages/quotation/components/QuotationSelectionContext';
import { useQuotationSubjectsBottomSheetFooterHeight } from '@euler/app/flows/task/pages/quotation/components/QuotationSubjectsBottomSheet';
import { kCustomQuotationTemplateCategoryId } from '@euler/app/flows/task/pages/quotation/functions/useQuotationTemplateConfig';
import { QuotationSubjectInfo } from '@euler/app/flows/task/pages/quotation/types/QuotationCatalog';
import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { withConfirmation } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { nanoid } from 'nanoid/non-secure';
import { memo, useCallback, useMemo } from 'react';
import { SectionList, TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components';

type Section = {
  title: string;
  data: QuotationSubjectInfo[];
  clearable?: boolean;
  emptyText?: string;
};

export const QuotationCatalogTabPage = wrapNavigatorScreen(
  ({ categoryId }: { categoryId: string }) => {
    const { rpx } = useResponsiveContext();

    const bottomSheetFooterHeight =
      useQuotationSubjectsBottomSheetFooterHeight();

    const { catalog } = useQuotationContext();

    const { selection$, toggleQuotationSubject } =
      useQuotationSelectionContext();

    const [selection] = useBehaviorSubject(selection$);

    const category = useMemo(
      () =>
        catalog.categories.find(x => x.id === categoryId) ?? {
          id: nanoid(),
          name: 'N/A',
          groups: [],
          template: {
            id: nanoid(),
            procedures: [],
          },
        },
      [catalog, categoryId],
    );

    const sections = useMemo<Section[]>(
      () =>
        category.groups.map(group => ({
          title: group.name,
          data: group.subjects,
          clearable:
            category.id === kCustomQuotationTemplateCategoryId &&
            category.groups.flatMap(g => g.subjects).length > 0,
          emptyText:
            category.id === kCustomQuotationTemplateCategoryId
              ? '暂无相关自定义报价项目'
              : undefined,
        })),
      [category],
    );

    const onClear = usePersistFn(async () => {
      if (
        await withConfirmation({
          title: '确认',
          message: '您即将清空所有自定义报价项目，该操作不可撤销，是否继续? ',
        })
      ) {
        // todo: implement clear custom quotation subjects
      }
    });

    const onDelete = usePersistFn(async (_item: QuotationSubjectInfo) => {
      if (
        await withConfirmation({
          title: '确认',
          message: '确认删除该自定义报价项目? ',
        })
      ) {
        // todo: implement delete custom quotation subject
      }
    });

    const onSubjectPress = useCallback(
      (subject: QuotationSubjectInfo) => {
        toggleQuotationSubject(subject.name);
      },
      [toggleQuotationSubject],
    );

    return (
      <>
        <SectionList
          sections={sections}
          keyExtractor={item => item.name}
          stickySectionHeadersEnabled={true}
          contentContainerStyle={{
            paddingBottom: bottomSheetFooterHeight + rpx(50),
          }}
          showsVerticalScrollIndicator={false}
          renderSectionHeader={({ section }) => (
            <SectionHeader
              title={section.title}
              count={section.data.length}
              clearable={section.clearable}
              onClear={onClear}
            />
          )}
          renderSectionFooter={({ section }) =>
            section.data.length === 0 ? (
              <Typography.Label
                css={`
                  margin: 0 ${rpx(25)}px;
                `}
                light
                color="#999"
                size={rpx(12)}
              >
                {section.emptyText ?? `暂无相关报价项目`}
              </Typography.Label>
            ) : (
              <></>
            )
          }
          renderItem={({ item, section, index }) => (
            <Cell
              item={item}
              deletable={section.clearable}
              selected={selection.includes(item.name)}
              onPress={onSubjectPress}
              onDelete={onDelete}
              first={index === 0}
              last={index === section.data.length - 1}
            />
          )}
          css={`
            flex: 1;
            background-color: #f3f3f3;
          `}
        />
      </>
    );
  },
);

const SectionHeader = memo(
  ({
    title,
    count,
    clearable,
    onClear,
  }: {
    title: string;
    count: number;
    clearable?: boolean;
    onClear?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      construction: {
        schedule: {
          workItem: {
            listSection: { bgColor, titleColor },
          },
        },
      },
    } = useTheme();
    return (
      <View
        css={`
          padding: ${rpx(15)}px ${rpx(25)}px;
          background-color: ${bgColor};
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <Typography.Label
          bold
          size={rpx(16)}
          css={`
            color: ${titleColor};
          `}
        >{`${title} (${count}项)`}</Typography.Label>
        {clearable ? (
          <TouchableOpacity onPress={onClear}>
            <Typography.Label bold color={StatusColors.Danger} size={rpx(14)}>
              清空
            </Typography.Label>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  },
);

const Cell = memo(
  ({
    item,
    selected,
    deletable,
    onPress,
    onDelete,
    first,
    last,
  }: {
    first?: boolean;
    last?: boolean;
    item: QuotationSubjectInfo;
    selected: boolean;
    deletable?: boolean;
    onPress: (item: QuotationSubjectInfo) => void;
    onDelete: (item: QuotationSubjectInfo) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      construction: {
        schedule: {
          workItem: {
            cell: { textLight, bgLight },
          },
        },
      },
    } = useTheme();

    const onCellPress = useCallback(() => {
      onPress?.(item);
    }, [item, onPress]);

    const onDeletePress = useCallback(() => {
      onDelete?.(item);
    }, [item, onDelete]);

    return (
      <QuotationCatalogSubjectCell
        title={item.name}
        selected={selected}
        deletable={deletable}
        onDelete={onDeletePress}
        onPress={onCellPress}
        onRemove={onCellPress}
        onAdd={onCellPress}
        titleStyle={{
          color: textLight,
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(15),
        }}
        css={`
          background-color: ${bgLight};
          margin: ${rpx(5)}px ${rpx(10)}px;
          margin-top: ${first ? 0 : rpx(5)}px;
          margin-bottom: ${rpx(last ? 15 : 5)}px;
        `}
      />
    );
  },
);
